// DEBUG-ONLY route /debug/bots: a bot playground. Play against the parametrized
// bots (arrow keys), add/remove them, and edit their levels + personality traits
// LIVE (sliders write straight into the running AI's aiData, so a change is felt
// within one replan). Roster changes (add/remove) apply at the next round, which
// starts automatically. See docs/design/bots/PERSONALITIES.md for the traits.
import React, { useEffect, useRef, useState } from "react";

import Match from "core/src/core/match.js";
import { wormColors } from "core/src/core/constants.js";
import * as gsf from "core/src/core/game-state-functions.js";
import * as random from "core/src/core/util/random.js";
import { skillParams, getEngineInfo, confusionTime } from "core/src/ai/path-checker-ai-handler.js";
import * as boardModel from "core/src/ai/board-model.js";
import { DUEL_BASELINE } from "core/src/ai/duel-baseline.js";
import { arenaValue, sizeKeyForPlayers } from "core/src/core/arena-sizes.js";
import { BOT_POOL, POOL_ELO_MIN, POOL_ELO_MAX, pickPoolBot, personaWord, type PoolBot } from "../../shared/bot-pool";

import GameCanvas from "../../game-canvas/GameCanvas";
import PowerUpToken from "../../shared/PowerUpToken";
import { POWERUPS, ALL_POWERUP_KEYS, SPAWN_RATES } from "../../shared/powerups";
import ReplayGameHandler from "../play/replay/replay-game-handler.js";
import LocalGameHandler from "../../shared/local-game-handler.js";
import PlayerSteeringListener from "../play/game/player-steering-listener.js";

// Arena size: fill the viewport height (the panel owns the right edge).
const CANVAS = typeof window !== "undefined" ? Math.min(Math.max(560, window.innerHeight - 110), 940) : 700;
const STORE_KEY = "ar.debugBots";
const POWERUPS_KEY = "ar.debugBots.powerUps"; // "1" = on; default off
const PU_ONLY_KEY = "ar.debugBots.puOnly";    // JSON array of the power-up keys that spawn; absent = all of them
const PU_RATE_KEY = "ar.debugBots.puRate";    // spawn-rate label (SPAWN_RATES); absent = Normal
const PLAYER_KEY = "ar.debugBots.player";     // "0" = bots only; default on
const OVERLAY_KEY = "ar.debugBots.overlay";   // board-model overlay mode
// Board-model overlays: what the bots actually read, drawn over the arena.
const OVERLAYS = ["none", "space", "clearance"] as const;
type Overlay = typeof OVERLAYS[number];
const PER_WORM: Overlay[] = ["clearance"];
// Space overlay: the combined field (max of the four) or one cardinal field.
// Shared by all worms, so no worm selection.
const SPACE_DIRS = ["combined", "right", "down", "left", "up"] as const;
type SpaceDir = typeof SPACE_DIRS[number];

// Trait sliders: [min, max]. Both signed. Caution: positive keeps clear of
// walls and trails, negative prefers skimming them (as long as the path
// survives). Aggression: positive seeks cut-offs (crossing an opponent's
// projected path ahead of them), negative keeps its distance from opponents.
// The rest of the catalog (wallAffinity, hunting, opportunism) is parked in
// the engine (ACTIVE_TRAITS in path-checker-ai-handler.js).
const TRAITS: [string, number, number][] = [
  ["planning", 0, 1],
  ["breadth", 0, 1],
  ["precision", 0, 1],
  ["caution", -1, 1],
  ["aggression", -1, 1],
  ["space", -1, 1],
  ["greed", -1, 1],
];
// What a trait is called on screen (the keys stay as the planner reads them).
const TRAIT_LABEL: Record<string, string> = { planning: "planning depth", breadth: "planning breadth", greed: "power-up greed" };
const ACTIVE = new Set(TRAITS.map(([n]) => n));
const ALL_KEYS = TRAITS.map(([n]) => n);
// A trait's value when absent from the config: the three "how good" dials have
// their own defaults, everything else is 0. A slider AT its default is stored
// as absent.
const TRAIT_DEFAULT: Record<string, number> = { planning: 0.7, precision: 0.7, breadth: 1 };
// The "how good" traits; shown first.
const SPECIAL = new Set(["planning", "breadth", "precision"]);
const traitValue = (t: Record<string, number>, name: string): number => (t[name] === undefined ? (TRAIT_DEFAULT[name] ?? 0) : t[name]);

// `pool` is set when the bot was seated from the rated pool: its id, measured
// Elo and stats, plus the traits it had then (so edits show as "edited").
interface BotCfg { id: string; traits: Record<string, number>; colorId: string; pool?: { id: string; elo: number; rounds?: number; winRate?: number; traits: Record<string, number> } }

const PRESETS: Record<string, Partial<Record<string, number>>> = {
  plain: {},
  careful: { caution: 0.6 },
  daredevil: { caution: -0.6 },
  hunter: { aggression: 0.7 },
  coward: { caution: 0.5, aggression: -0.6 },
  explorer: { space: 0.8 },
  greedy: { greed: 0.8 },
  // the best 1v1 bot so far (core ai/duel-baseline.js)
  baseline: { ...DUEL_BASELINE.traits },
};

// What the planning / precision dials actually set in the engine (skillParams),
// as the sliders' hover text.
function levelTooltip(name: string, value: number): string | undefined {
  if (name === "planning") {
    const sp = skillParams(undefined, { planning: value, precision: 1 });
    return [
      `lookahead ${sp.simDuration.toFixed(2)}s`,
    ].join("\n");
  }
  if (name === "breadth") {
    const sp = skillParams(undefined, { planning: 1, precision: 1, breadth: value });
    return `weighs ${sp.fanCount} turn options each time it thinks (3 to 9; a crowd level may cap it)`;
  }
  if (name === "precision") {
    const sp = skillParams(undefined, { planning: 1, precision: value });
    return [
      `reaction time ${Math.round(sp.replanInterval * 1000)}ms`,
      `steering changes land up to ${Math.round(sp.steerDelay * 1000)}ms late (random)`,
      `reversed keys: presses the wrong key for ${confusionTime(value, true).toFixed(2)}s when they flip, ${confusionTime(value, false).toFixed(2)}s when they flip back`,
    ].join("\n");
  }
  return undefined;
}

// Drop parked traits from a stored bot (older saves may carry them), so the
// summary and the engine agree on what the bot actually is.
function activeTraits(t: Record<string, number> | undefined): Record<string, number> {
  const out: Record<string, number> = {};
  for (const [k, v] of Object.entries(t || {})) if (ACTIVE.has(k) && typeof v === "number" && v !== (TRAIT_DEFAULT[k] ?? 0)) out[k] = v;
  return out;
}

// /debug/bots?vs={"name":"Oxford","traits":{...}} (Bot management's "Play 1v1"):
// the roster becomes exactly that one bot, with the player worm on, so the page
// opens as a duel against it. An ARRAY of those seats several (a test line-up,
// e.g. the same bot at three precisions). Stored like any roster, so a reload
// keeps it.
function duelFromUrl(): BotCfg[] | null {
  try {
    const raw = new URLSearchParams(window.location.search).get("vs");
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    const list = (Array.isArray(parsed) ? parsed : [parsed]).filter((v) => v && typeof v.name === "string" && v.traits && typeof v.traits === "object").slice(0, 8);
    if (!list.length) return null;
    const colors = ["pink", "green", "orange", "purple", "lime", "teal", "red", "yellow"];
    const seen = new Set<string>();
    const bots: BotCfg[] = list.map((v, i) => {
      let id = v.name.slice(0, 24);
      while (seen.has(id)) id += "x";
      seen.add(id);
      return { id, traits: activeTraits(v.traits), colorId: colors[i % colors.length] };
    });
    localStorage.setItem(STORE_KEY, JSON.stringify(bots));
    localStorage.setItem(PLAYER_KEY, "1");
    return bots;
  } catch {
    return null;
  }
}

function loadBots(): BotCfg[] {
  const duel = duelFromUrl();
  if (duel) return duel;
  try {
    const v = JSON.parse(localStorage.getItem(STORE_KEY) || "");
    if (Array.isArray(v) && v.length) {
      // De-dupe ids defensively: two players sharing an id crashes game-factory
      // (the AI gets attached twice to the same gameState player).
      const seen = new Set<string>();
      const out: BotCfg[] = [];
      for (const raw of v as (BotCfg & { skill?: number })[]) {
        let id = raw.id;
        while (seen.has(id)) id = id + "x";
        seen.add(id);
        const traits = activeTraits(raw.traits);
        // Migration: an old stored skill (1-10) becomes planning + precision.
        if (typeof raw.skill === "number" && traits.planning === undefined && traits.precision === undefined) {
          const lvl = Math.round(((raw.skill - 1) / 9) * 20) / 20;
          if (lvl !== TRAIT_DEFAULT.planning) traits.planning = lvl;
          if (lvl !== TRAIT_DEFAULT.precision) traits.precision = lvl;
        }
        const b: BotCfg = { id, traits, colorId: raw.colorId, pool: raw.pool };
        // Migration: older stored bots carry no colour; assign a free one.
        if (!b.colorId || !COLORS.includes(b.colorId)) b.colorId = freeColor(out);
        out.push(b);
      }
      return out;
    }
  } catch { /* fresh */ }
  return [
    { id: "bot1", traits: { ...PRESETS.careful } as Record<string, number>, colorId: "pink" },
    { id: "bot2", traits: { ...PRESETS.daredevil } as Record<string, number>, colorId: "green" },
  ];
}

// Valid core worm colour ids (the human takes blue). Each bot OWNS its colour
// (stored on its config), so list order never re-colours anyone.
const COLORS = ["pink", "green", "orange", "purple", "lime", "teal", "indigo"];
function freeColor(bots: BotCfg[]): string {
  const used = bots.map((b) => b.colorId);
  const free = COLORS.find((c) => !used.includes(c));
  if (free) return free;
  // All taken: pick the least-used one.
  return COLORS.slice().sort((a, b) => used.filter((u) => u === a).length - used.filter((u) => u === b).length)[0];
}

export default function DebugBotsPage() {
  const [bots, setBots] = useState<BotCfg[]>(loadBots);
  const botsRef = useRef(bots);
  botsRef.current = bots;
  const [, force] = useState(0);
  const seqRef = useRef(100);
  const handlerRef = useRef<any>(null);
  const gameRef = useRef<any>(null);
  const playersRef = useRef<any[]>([]);
  const [round, setRound] = useState(0);
  const [expanded, setExpanded] = useState<Record<string, boolean>>({});
  const [paused, setPaused] = useState(false);
  const manualPauseRef = useRef(false);
  const [addOpen, setAddOpen] = useState(false);
  // Power-ups on/off (persisted, default off). Applies live to the running
  // round's spawn rate (tokens already on the board stay) and to every new round.
  const [powerUps, setPowerUps] = useState<boolean>(() => {
    try {
      // ?powerups=... in the URL means "on" (the picker below reads which ones)
      if (new URLSearchParams(window.location.search).get("powerups")) { localStorage.setItem(POWERUPS_KEY, "1"); return true; }
      return localStorage.getItem(POWERUPS_KEY) === "1";
    } catch { return false; }
  });
  const powerUpsRef = useRef(powerUps);
  powerUpsRef.current = powerUps;
  const togglePowerUps = () => {
    const next = !powerUps;
    setPowerUps(next);
    try { localStorage.setItem(POWERUPS_KEY, next ? "1" : "0"); } catch { /* ignore */ }
    applyPowerUps(next, puOnlyRef.current, puRateRef.current);
  };
  // WHICH power-ups spawn, and how often (persisted; also set by the URL:
  // ?powerups=key_switch,drunk&rate=Chaos). For testing one effect at a time,
  // e.g. only Reversed keys at Chaos to watch how bots of different precision
  // cope. Applies live, like the on/off switch.
  const [puOnly, setPuOnly] = useState<string[]>(() => {
    try {
      const q = new URLSearchParams(window.location.search).get("powerups");
      if (q) {
        const keys = q.split(",").filter((k) => ALL_POWERUP_KEYS.includes(k));
        if (keys.length) { localStorage.setItem(PU_ONLY_KEY, JSON.stringify(keys)); localStorage.setItem(POWERUPS_KEY, "1"); return keys; }
      }
      const v = JSON.parse(localStorage.getItem(PU_ONLY_KEY) || "null");
      return Array.isArray(v) ? v.filter((k) => ALL_POWERUP_KEYS.includes(k)) : ALL_POWERUP_KEYS.slice();
    } catch { return ALL_POWERUP_KEYS.slice(); }
  });
  const [puRate, setPuRate] = useState<string>(() => {
    try {
      const q = new URLSearchParams(window.location.search).get("rate");
      const hit = q && SPAWN_RATES.find((r) => r.label.toLowerCase() === q.toLowerCase() && r.value > 0);
      if (hit) { localStorage.setItem(PU_RATE_KEY, hit.label); return hit.label; }
      const v = localStorage.getItem(PU_RATE_KEY);
      return v && SPAWN_RATES.some((r) => r.label === v && r.value > 0) ? v : "Normal";
    } catch { return "Normal"; }
  });
  const puOnlyRef = useRef(puOnly); puOnlyRef.current = puOnly;
  const puRateRef = useRef(puRate); puRateRef.current = puRate;
  const spawnChanceFor = (on: boolean, rate: string) => (on ? (SPAWN_RATES.find((r) => r.label === rate)?.value ?? undefined) : 0);
  const disabledFor = (only: string[]) => ALL_POWERUP_KEYS.filter((k) => !only.includes(k));
  function applyPowerUps(on: boolean, only: string[], rate: string) {
    const gs = gameRef.current?.gameState;
    if (!gs) return;
    gs.powerUpSpawnChance = spawnChanceFor(on, rate);
    gs.disabledPowerUps = disabledFor(only);
  }
  const togglePuKey = (key: string) => {
    const next = puOnly.includes(key) ? puOnly.filter((k) => k !== key) : [...puOnly, key];
    setPuOnly(next);
    try { localStorage.setItem(PU_ONLY_KEY, JSON.stringify(next)); } catch { /* ignore */ }
    applyPowerUps(powerUpsRef.current, next, puRateRef.current);
  };
  const setOnlyKeys = (next: string[]) => {
    setPuOnly(next);
    try { localStorage.setItem(PU_ONLY_KEY, JSON.stringify(next)); } catch { /* ignore */ }
    applyPowerUps(powerUpsRef.current, next, puRateRef.current);
  };
  const changeRate = (label: string) => {
    setPuRate(label);
    try { localStorage.setItem(PU_RATE_KEY, label); } catch { /* ignore */ }
    applyPowerUps(powerUpsRef.current, puOnlyRef.current, label);
  };
  // Human worm in the roster (persisted, default on). The roster is built per
  // round, so this applies on the next Restart.
  const [player, setPlayer] = useState<boolean>(() => { try { return localStorage.getItem(PLAYER_KEY) !== "0"; } catch { return true; } });
  const playerRef = useRef(player);
  playerRef.current = player;
  const togglePlayer = () => {
    const next = !player;
    setPlayer(next);
    try { localStorage.setItem(PLAYER_KEY, next ? "1" : "0"); } catch { /* ignore */ }
  };
  // Board-model overlay (persisted). Per-worm fields need a chosen worm.
  const [overlay, setOverlay] = useState<Overlay>(() => { try { const v = localStorage.getItem(OVERLAY_KEY) as Overlay; return OVERLAYS.includes(v) ? v : "none"; } catch { return "none"; } });
  const [overlayWorm, setOverlayWorm] = useState<string>("");
  const [spaceDir, setSpaceDir] = useState<SpaceDir>("combined");
  const overlayCanvasRef = useRef<HTMLCanvasElement | null>(null);
  const changeOverlay = (v: Overlay) => { setOverlay(v); try { localStorage.setItem(OVERLAY_KEY, v); } catch { /* ignore */ } };
  // Replay of the last finished round, shown ON THE SAME BOARD (no layout swap):
  // the arena canvas renders the recorded gameState on the replay clock, and
  // exiting returns to exactly the paused/over state the live game was left in.
  const [mode, setMode] = useState<"live" | "replay">("live");
  const modeRef = useRef(mode);
  modeRef.current = mode;
  const replayRef = useRef<{ match: any; roundId: number } | null>(null);
  const replayGameRef = useRef<any>(null);
  const resumeOnExitRef = useRef(false);
  const [roundOver, setRoundOver] = useState(false);
  const [, setReplayTick] = useState(0);
  const doneCheckRef = useRef<any>(null);

  const forceUi = () => force((t) => t + 1);
  const persist = (next: BotCfg[]) => {
    setBots(next);
    try { localStorage.setItem(STORE_KEY, JSON.stringify(next)); } catch { /* ignore */ }
  };

  // One round: build a fresh Match from the CURRENT roster (so add/remove apply),
  // start it, auto-roll the next round shortly after it ends.
  const startRound = () => {
    if (handlerRef.current) handlerRef.current.stop();
    const players = [
      ...(playerRef.current ? [{ id: "you", name: "You", colorId: "blue", type: "human" }] : []),
      ...botsRef.current.map((b) => ({ id: b.id, name: b.id, colorId: b.colorId, type: "bot", traits: b.traits })),
    ];
    playersRef.current = players;
    const match = Match({ matchConfig: {
      players,
      // the arena a real match of this many players gets (core arena-sizes.js):
      // a 1v1 is the Small rectangle or stadium, like a ranked duel
      map: gsf.createMapFromValue(arenaValue(Math.random() < 0.5 ? "rectangle" : "stadium", sizeKeyForPlayers(players.length))),
      maxScore: 999999,
      // Rounds here restart all the time: a 2 second countdown and no roll call,
      // instead of the real game's roll call + 3 seconds.
      startPhaseDuration: 2,
      skipRollCall: true,
      // 0 = no power-ups at all; else the chosen rate, and only the chosen power-ups.
      powerUpSpawnChance: spawnChanceFor(powerUpsRef.current, puRateRef.current),
      disabledPowerUps: disabledFor(puOnlyRef.current),
    } });
    const game = match.prepareNextGame(random.generateSeed());
    gameRef.current = game;
    // Round-finished bookkeeping: record the replay and schedule the next round.
    // Called from the handler's onGameOver AND from a low-rate interval fallback
    // (setInterval runs even in a hidden tab, where the rAF-driven handler
    // doesn't), guarded so it runs once per round.
    let finished = false;
    const roundFinished = () => {
      if (finished) return;
      finished = true;
      if (doneCheckRef.current) { clearInterval(doneCheckRef.current); doneCheckRef.current = null; }
      try {
        match.addFinishedGameState(game.gameState);
        replayRef.current = { match, roundId: match.matchState.roundsData.length - 1 };
      } catch { /* replay is best-effort */ }
      setRoundOver(true);
      forceUi();
      // No auto-restart: the finished board stays up (with the Replay button)
      // until Restart is pressed.
    };
    if (doneCheckRef.current) clearInterval(doneCheckRef.current);
    doneCheckRef.current = setInterval(() => { if (!game.isActive()) roundFinished(); }, 500);
    const handler = LocalGameHandler({
      game,
      onGameOver: roundFinished,
    });
    handlerRef.current = handler;
    handler.start();
    manualPauseRef.current = false;
    setPaused(false);
    setRoundOver(false);
    (window as any).__botsDebug = { game, handler };
    setRound((r) => r + 1);
  };

  useEffect(() => {
    startRound();
    // Key names are key-util's normalized ids ("LEFT"/"RIGHT", "A"/"D"), not DOM
    // key values. Two listeners so both the arrows and A/D steer, like real play.
    const steer = (s: number) => {
      const gs = gameRef.current?.gameState;
      if (gs && gs.players.some((p: any) => p.id === "you")) gsf.setPlayerSteering(gs, "you", s);
    };
    const listener = PlayerSteeringListener();
    listener.addKeyListeners({ left: "LEFT", right: "RIGHT", onSteeringUpdate: steer });
    const listenerAD = PlayerSteeringListener();
    listenerAD.addKeyListeners({ left: "A", right: "D", onSteeringUpdate: steer });
    // Pause while the tab is hidden: rAF stops but the wall clock doesn't, and
    // the handler would otherwise apply the whole hidden gap as ONE giant dt
    // (the AI then follows a stale turn for seconds - instant death spirals).
    const onVis = () => {
      const h = handlerRef.current;
      if (!h) return;
      if (document.visibilityState === "hidden") h.pause();
      else if (!manualPauseRef.current) h.resume();
    };
    document.addEventListener("visibilitychange", onVis);
    return () => {
      if (doneCheckRef.current) clearInterval(doneCheckRef.current);
      document.removeEventListener("visibilitychange", onVis);
      listener.removeKeyListeners();
      listenerAD.removeKeyListeners();
      if (handlerRef.current) handlerRef.current.stop();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Live edit: write into BOTH the persisted config and the RUNNING game's aiData.
  // The traits object is handed to the AI by reference at round start, so mutating
  // the same object mid-round takes effect on the bot's next replan.
  const editBot = (id: string, fn: (b: BotCfg) => void) => {
    const next = botsRef.current.map((b) => ({ ...b, traits: b.traits }));
    const target = next.find((b) => b.id === id);
    if (!target) return;
    fn(target);
    persist(next);
    const gs = gameRef.current?.gameState;
    const player = gs && gs.players.find((p: any) => p.id === id);
    if (player && player.aiData) {
      player.aiData.traits = target.traits;
      // Drop the current plan so the bot re-picks a path under the new
      // personality on its next tick, instead of riding out the old one
      // until survival forces a replan.
      if (player.aiData.trajectory) player.aiData.trajectory = [];
    }
  };

  // A fresh unique id: the counter alone is not enough (it resets on reload
  // while stored bots keep their old ids), so skip past collisions.
  const nextId = () => {
    let id = "bot" + seqRef.current++;
    while (botsRef.current.some((b) => b.id === id)) id = "bot" + seqRef.current++;
    return id;
  };

  // Seat a rated pool bot near this Elo (distinct from ones already seated).
  const [poolElo, setPoolElo] = useState(1500);
  const addPoolBot = (elo: number) => {
    const used = new Set(botsRef.current.map((b) => b.pool?.id).filter((x): x is string => !!x));
    const pb: PoolBot | null = pickPoolBot(elo, used);
    if (!pb) return;
    persist([...botsRef.current, { id: nextId(), traits: activeTraits(pb.traits), colorId: freeColor(botsRef.current), pool: { id: pb.id, elo: pb.elo, rounds: pb.rounds, winRate: pb.winRate, traits: activeTraits(pb.traits) } }]);
  };

  const addBot = (preset: string) => {
    persist([...botsRef.current, { id: nextId(), traits: { ...(PRESETS[preset] || {}) } as Record<string, number>, colorId: freeColor(botsRef.current) }]);
  };

  // Summary line for a collapsed card: the persona at a glance. Nearest preset
  // name when the traits match one, else the nonzero trait weights.
  const summarize = (b: BotCfg): string => {
    if (b.pool) {
      const same = ALL_KEYS.every((k) => (b.traits[k] ?? TRAIT_DEFAULT[k] ?? 0) === (b.pool!.traits[k] ?? TRAIT_DEFAULT[k] ?? 0));
      const stats = `${b.pool.rounds ?? "?"} rounds · ${b.pool.winRate != null ? Math.round(b.pool.winRate * 100) + "% of pairings won" : "no stats"}`;
      return (same ? "" : "EDITED · ") + personaWord(b.pool.traits) + " · " + stats;
    }
    const entries = TRAITS.filter(([n]) => !SPECIAL.has(n)).map(([n]) => [n, traitValue(b.traits, n)] as const).filter(([n, v]) => v !== (TRAIT_DEFAULT[n] ?? 0));
    if (!entries.length) return "plain survivor, no personality";
    for (const [pname, pt] of Object.entries(PRESETS)) {
      const keys = Object.keys(pt);
      if (keys.length && keys.length === entries.length && keys.every((k) => Math.abs((pt as any)[k] - traitValue(b.traits, k)) < 0.001)) {
        return pname + " preset";
      }
    }
    return entries.map(([n, v]) => n + " " + (v > 0 ? "+" : "") + v.toFixed(1)).join(" · ");
  };

  const enterReplay = () => {
    if (!replayRef.current) return;
    // Remember whether the live game was running, so Exit restores it exactly.
    resumeOnExitRef.current = !paused && !roundOver;
    if (resumeOnExitRef.current && handlerRef.current) handlerRef.current.pause();
    const recorded = replayRef.current.match.matchState.roundsData[replayRef.current.roundId].gameState;
    const rg = ReplayGameHandler({ gameState: recorded, onReplayOver: () => {}, onEnded: () => setReplayTick((t) => t + 1) });
    replayGameRef.current = rg;
    rg.start();
    setMode("replay");
  };
  const exitReplay = () => {
    if (replayGameRef.current) { try { replayGameRef.current.stop(); } catch { /* done */ } replayGameRef.current = null; }
    setMode("live");
    if (resumeOnExitRef.current && handlerRef.current && !manualPauseRef.current) handlerRef.current.resume();
  };

  // Overlay redraw: 10Hz from the live game's board model (the same one the
  // bots read; asking for it refreshes it if stale, exactly as the AI would).
  useEffect(() => {
    if (overlay === "none") return;
    const draw = () => {
      const cv = overlayCanvasRef.current;
      const gs = gameRef.current?.gameState;
      if (!cv || !gs) return;
      const ctx = cv.getContext("2d");
      if (!ctx) return;
      ctx.clearRect(0, 0, cv.width, cv.height);
      const b: any = boardModel.getBoardModel(gs);
      const scale = cv.width / (b.cols * b.tileSize);
      const ts = b.tileSize * scale;
      const n = b.cols * b.rows;
      const colorOf = (wi: number) => {
        const pid = b.worms[wi]?.playerId;
        const pl = playersRef.current.find((p: any) => p.id === pid);
        return (wormColors as any)[pl?.colorId] || "#ffffff";
      };
      const wi = Math.max(0, b.worms.findIndex((w: any) => w.playerId === overlayWorm));
      const entry = b.worms[wi];
      const e = getEngineInfo();
      for (let i = 0; i < n; i++) {
        let fill: string | null = null;
        if (false) {        } else if (overlay === "space") {
          // Space ahead: one cardinal field, or the combined (max of the four).
          // Bright = deep free space ahead, dark = wall / pocket.
          let v: number;
          if (spaceDir === "combined") {
            v = Math.max(b.spaceDir[0][i], b.spaceDir[1][i], b.spaceDir[2][i], b.spaceDir[3][i]) / e.spaceCap;
          } else {
            const d = spaceDir === "right" ? 0 : spaceDir === "down" ? 1 : spaceDir === "left" ? 2 : 3;
            v = b.spaceDir[d][i] / e.spaceCap;
          }
          if (v > 0) { ctx.globalAlpha = 0.08 + 0.6 * v; fill = "#33ddaa"; }
        } else if (overlay === "clearance") {
          // Per-worm field (own head ignored), 0 = black .. reference = clear.
          const c = entry?.clearance ? entry.clearance[i] : b.clearance[i];
          const v = Math.min(1, c / e.cautionClearanceRef);
          ctx.globalAlpha = 0.55 * (1 - v); fill = "#000000";
        }
        if (fill) {
          ctx.fillStyle = fill;
          ctx.fillRect((i % b.cols) * ts, Math.floor(i / b.cols) * ts, ts + 0.5, ts + 0.5);
        }
      }
      ctx.globalAlpha = 1;
      // Mark the chosen worm for per-worm fields.
      if (PER_WORM.includes(overlay) && entry) {
        ctx.strokeStyle = colorOf(wi); ctx.lineWidth = 2;
        ctx.beginPath(); ctx.arc(entry.x * scale, entry.y * scale, 10, 0, Math.PI * 2); ctx.stroke();
      }
    };
    draw();
    const t = setInterval(draw, 100);
    return () => clearInterval(t);
  }, [overlay, overlayWorm, spaceDir, round]);

  // Scrubber refresh while a replay runs.
  useEffect(() => {
    if (mode !== "replay") return;
    const t = setInterval(() => setReplayTick((v) => v + 1), 200);
    return () => clearInterval(t);
  }, [mode]);

  const rg = replayGameRef.current;
  const inReplay = mode === "replay" && rg;
  const recordedGs = inReplay && replayRef.current ? replayRef.current.match.matchState.roundsData[replayRef.current.roundId].gameState : null;

  const row = { display: "flex", alignItems: "center", gap: 8, fontSize: 13 } as const;
  return (
    <div style={{ color: "#cbd3e6", fontFamily: "'Rajdhani',sans-serif", background: "#070a12", height: "100vh", display: "flex", overflow: "hidden" }}>
      {/* Arena: centered in the remaining space left of the panel. */}
      <div style={{ flex: 1, position: "relative", display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: 10 }}>
        {/* Round settings: pinned to the arena's top-right corner (out of the
            centered flow, so toggling never moves the board). */}
        <div style={{ position: "absolute", top: 24, right: 28, display: "flex", flexDirection: "column", gap: 6, fontSize: 13, color: "#8fa0c0", userSelect: "none" }}>
          <label style={{ display: "flex", alignItems: "center", gap: 8, cursor: "pointer" }}>
            <input type="checkbox" checked={powerUps} onChange={togglePowerUps} />
            Power-ups <span style={{ color: powerUps ? "#7CFC66" : "#6b7590", fontWeight: 700, width: 22 }}>{powerUps ? "on" : "off"}</span>
          </label>
          <label style={{ display: "flex", alignItems: "center", gap: 8, cursor: "pointer" }} title="applies on the next Restart">
            <input type="checkbox" checked={player} onChange={togglePlayer} />
            Player worm <span style={{ color: player ? "#7CFC66" : "#6b7590", fontWeight: 700, width: 22 }}>{player ? "on" : "off"}</span>
          </label>
          {/* Board-model overlay: what the bots read, drawn over the arena. */}
          <label style={{ display: "flex", alignItems: "center", gap: 8 }}>
            Overlay
            <select value={overlay} onChange={(ev) => changeOverlay(ev.target.value as Overlay)} style={selectStyle}>
              {OVERLAYS.map((o) => <option key={o} value={o}>{o}</option>)}
            </select>
          </label>
          <label style={{ display: "flex", alignItems: "center", gap: 8, visibility: PER_WORM.includes(overlay) ? "visible" : "hidden" }}>
            for
            <select value={overlayWorm} onChange={(ev) => setOverlayWorm(ev.target.value)} style={selectStyle}>
              {playersRef.current.map((p: any) => <option key={p.id} value={p.id}>{p.id}</option>)}
            </select>
          </label>
          <label style={{ display: "flex", alignItems: "center", gap: 8, visibility: overlay === "space" ? "visible" : "hidden" }}>
            dir
            <select value={spaceDir} onChange={(ev) => setSpaceDir(ev.target.value as SpaceDir)} style={selectStyle}>
              {SPACE_DIRS.map((d) => <option key={d} value={d}>{d}</option>)}
            </select>
          </label>
        </div>
        <h2 style={{ margin: 0, fontFamily: "'Bungee',sans-serif", fontSize: 20 }}>Bot playground</h2>
        <div style={{ position: "relative", width: CANVAS, height: CANVAS, background: "#0a0e18", border: "1px solid #26304a", borderRadius: 8, overflow: "hidden", flex: "0 0 auto" }}>
          {inReplay && recordedGs ? (
            <GameCanvas
              key={"replay" + round}
              config={{ width: CANVAS, height: CANVAS }}
              gameState={recordedGs}
              players={playersRef.current}
              renderTime={rg.getReplayTime}
            />
          ) : gameRef.current ? (
            <GameCanvas
              key={round}
              config={{ width: CANVAS, height: CANVAS }}
              gameState={gameRef.current.gameState}
              players={playersRef.current}
            />
          ) : null}
          {/* Board-model overlay: transparent canvas over the arena (live game only). */}
          {overlay !== "none" && !inReplay && (
            <canvas ref={overlayCanvasRef} width={CANVAS} height={CANVAS}
              style={{ position: "absolute", left: 0, top: 0, width: CANVAS, height: CANVAS, pointerEvents: "none", zIndex: 1000 /* above GameCanvas's stacked layers (z-index up to 500) */ }} />
          )}
        </div>
        {/* Board controls: live = restart/pause (+ replay when halted); replay =
            play-pause + scrubber + exit, all on the same board. Both modes render
            inside one fixed-size slot (and the buttons have a fixed height), so
            switching to/from replay never re-centers the arena. */}
        <div style={{ width: CANVAS, height: BOARD_BTN_H, flex: "0 0 auto", display: "flex", alignItems: "center", justifyContent: "center" }}>
        {inReplay ? (
          <div style={{ display: "flex", gap: 10, alignItems: "center", width: "100%" }}>
            <button
              style={{ ...boardBtnStyle, width: 112 }}
              onClick={() => {
                if (rg.isEnded()) rg.restart();
                else if (rg.isPaused()) rg.resume();
                else rg.pause();
                setReplayTick((t) => t + 1);
              }}
            >
              <span style={{ fontSize: 15 }}>{rg.isEnded() ? "\u21BB" : rg.isPaused() ? "\u25B6" : "\u23F8"}</span>
              {rg.isEnded() ? " Again" : rg.isPaused() ? " Play" : " Pause"}
            </button>
            <input
              type="range" min={0} max={1000} value={Math.round((rg.getReplayProgress() || 0) * 1000)}
              style={{ flex: 1 }}
              onChange={(e) => { rg.setReplayProgress(Number(e.target.value) / 1000); setReplayTick((t) => t + 1); }}
            />
            <button onClick={exitReplay} style={{ ...boardBtnStyle, borderColor: "#f5c54266", color: "#f5c542" }}>
              <span style={{ fontSize: 15 }}>&#10005;</span> Exit replay
            </button>
          </div>
        ) : (
          <div style={{ display: "flex", gap: 10, alignItems: "center" }}>
            <button onClick={() => startRound()} style={boardBtnStyle}>
              <span style={{ fontSize: 17 }}>&#8635;</span> Restart
            </button>
            <button
              style={{ ...boardBtnStyle, width: 132, color: paused ? "#7CFC66" : "#cbd3e6" }}
            onClick={() => {
              const h = handlerRef.current;
              if (!h) return;
              if (paused) { manualPauseRef.current = false; h.resume(); setPaused(false); }
              else { manualPauseRef.current = true; h.pause(); setPaused(true); }
            }}
            >
              <span style={{ fontSize: 15 }}>{paused ? "\u25B6" : "\u23F8"}</span> {paused ? "Resume" : "Pause"}
            </button>
            {/* Always in the DOM (visibility toggled) so the row never re-centers
                when Replay appears. Shown while paused or the round is over. */}
            <button
              title="watch the last finished round"
              style={{ ...boardBtnStyle, width: 128, borderColor: "#f5c54266", color: "#f5c542", visibility: (paused || roundOver) && replayRef.current ? "visible" : "hidden" }}
              onClick={enterReplay}
            >
              <span style={{ fontSize: 16 }}>&#10226;</span> Replay
            </button>
          </div>
        )}
        </div>
      </div>
      {/* Bot panel: fixed-width column, scrolls internally so expanding a card
          never moves the arena. */}
      <div style={{ width: 420, flex: "0 0 auto", height: "100vh", overflowY: "auto", padding: "16px 16px 24px", borderLeft: "1px solid #1a2138", boxSizing: "border-box", display: "flex", flexDirection: "column" }}>
        <div style={{ flex: 1 }}>
        {/* which power-ups spawn, and how often (only while power-ups are on). Here in
          the panel, not over the arena's corner, so it never covers the board. */}
        {powerUps && (
          <div style={{ display: "flex", flexDirection: "column", gap: 5, padding: "6px 8px", border: "1px solid #1a2138", borderRadius: 8, background: "rgba(10,14,24,0.85)", margin: "0 0 14px" }}>
            <div style={{ display: "flex", flexWrap: "wrap", gap: 3 }}>
              {POWERUPS.map((p) => (
                <button key={p.key} type="button" onClick={() => togglePuKey(p.key)} title={p.name + (puOnly.includes(p.key) ? " (spawns)" : " (off)")} aria-pressed={puOnly.includes(p.key)} style={{ all: "unset", cursor: "pointer", lineHeight: 0, borderRadius: "50%", padding: 1, border: "1px solid " + (puOnly.includes(p.key) ? "rgba(255,255,255,0.55)" : "transparent"), opacity: puOnly.includes(p.key) ? 1 : 0.28, filter: puOnly.includes(p.key) ? "none" : "grayscale(1)" }}>
                  {/* the real token, in its own colour (green / red / blue), as it looks on the board */}
                  <PowerUpToken icon={p.icon} color={p.color} size={30} />
                </button>
              ))}
            </div>
            <div style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 12 }}>
              <button type="button" onClick={() => setOnlyKeys(ALL_POWERUP_KEYS.slice())} style={{ all: "unset", cursor: "pointer", color: "#28c4d4" }}>all</button>
              <button type="button" onClick={() => setOnlyKeys(["key_switch"])} style={{ all: "unset", cursor: "pointer", color: "#28c4d4" }} title="Only Reversed keys: watch how bots of different precision cope">only reversed keys</button>
              <select value={puRate} onChange={(e) => changeRate(e.target.value)} style={{ marginLeft: "auto", background: "#0a0e18", color: "#cbd3e6", border: "1px solid #26304a", borderRadius: 5, fontSize: 12, padding: "1px 4px" }} title="Spawn rate">
                {SPAWN_RATES.filter((r) => r.value > 0).map((r) => <option key={r.label} value={r.label}>{r.label}</option>)}
              </select>
            </div>
          </div>
        )}
        <h3 style={{ margin: 0, padding: "10px 0 20px", textAlign: "center", fontFamily: "'Bungee',sans-serif", fontSize: 15, color: "#e8eefc", letterSpacing: 1 }}>Bots</h3>
        {bots.map((b) => {
          const color = (wormColors as any)[b.colorId];
          const open = !!expanded[b.id];
          return (
            <div key={b.id} style={{ border: "1px solid #26304a", borderRadius: 10, marginBottom: 10, background: "#0a0e18", overflow: "hidden" }}>
              {/* Summary row: always visible, click to expand. */}
              <div
                onClick={() => setExpanded((e) => ({ ...e, [b.id]: !e[b.id] }))}
                style={{ display: "flex", alignItems: "center", gap: 10, padding: "10px 12px", cursor: "pointer", userSelect: "none" }}
              >
                <span style={{ width: 14, height: 14, borderRadius: "50%", background: color, flex: "0 0 auto", boxShadow: `0 0 8px ${color}88` }} />
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: 15, fontWeight: 700, color: "#e8eefc" }}>
                    {b.id} <span style={{ color: "#7CFC66", fontWeight: 600, fontSize: 13 }}>plan {traitValue(b.traits, "planning").toFixed(2)} · prec {traitValue(b.traits, "precision").toFixed(2)}{traitValue(b.traits, "breadth") !== 1 ? ` · breadth ${traitValue(b.traits, "breadth").toFixed(2)}` : ""}</span>
                    {b.pool && <span title={"rated pool bot " + b.pool.id} style={{ marginLeft: 8, color: "#f5c542", fontWeight: 700, fontSize: 12, background: "#f5c54222", borderRadius: 6, padding: "1px 6px" }}>Elo {b.pool.elo}</span>}
                  </div>
                  <div style={{ fontSize: 12.5, color: "#8fa0c0", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{summarize(b)}</div>
                </div>
                <span style={{ color: "#8fa0c0", fontSize: 14, flex: "0 0 auto", padding: "4px 6px" }}>{open ? "▾ less" : "▸ edit"}</span>
                <button
                  title="duplicate bot"
                  onClick={(e) => {
                    e.stopPropagation();
                    // Insert the copy right beneath its original.
                    const next = [...botsRef.current];
                    const at = next.findIndex((x) => x.id === b.id);
                    next.splice(at + 1, 0, { id: nextId(), traits: { ...b.traits }, colorId: freeColor(next), pool: b.pool });
                    persist(next);
                  }}
                  style={{ ...btnStyle, color: "#9fd4ff", padding: "6px 13px", fontSize: 16, flex: "0 0 auto" }}
                >⧉</button>
                <button
                  title="remove bot"
                  onClick={(e) => { e.stopPropagation(); persist(botsRef.current.filter((x) => x.id !== b.id)); }}
                  style={{ ...btnStyle, color: "#ff5c7a", padding: "6px 13px", fontSize: 16, flex: "0 0 auto" }}
                >✕</button>
              </div>
              {/* Expanded editor: skill + all trait sliders, roomy. */}
              {open && (
                <div style={{ padding: "2px 12px 12px", borderTop: "1px solid #1a2138" }}>
                  {TRAITS.map(([name, min, max]) => (
                    <label key={name} style={{ ...row, margin: "6px 0", borderBottom: name === "precision" ? "1px solid #1a2138" : undefined, paddingBottom: name === "precision" ? 8 : undefined }} title={levelTooltip(name, traitValue(b.traits, name))}>
                      <span style={{ width: 124, color: traitValue(b.traits, name) !== (TRAIT_DEFAULT[name] ?? 0) ? "#e8eefc" : "#6b7590" }}>{TRAIT_LABEL[name] || name}</span>
                      <input type="range" min={min} max={max} step={0.05} value={traitValue(b.traits, name)} style={{ flex: 1 }}
                        onChange={(e) => editBot(b.id, (t) => { const v = Number(e.target.value); if (v === (TRAIT_DEFAULT[name] ?? 0)) delete t.traits[name]; else t.traits[name] = v; })} />
                      <span style={{ width: 40, color: "#7CFC66", textAlign: "right", fontVariantNumeric: "tabular-nums" }}>{traitValue(b.traits, name).toFixed(2)}</span>
                    </label>
                  ))}
                </div>
              )}
            </div>
          );
        })}
        </div>
        {/* One ADD BOT button; the preset choices pop up above it (no layout shift). */}
        <div style={{ position: "relative", marginTop: 12, flex: "0 0 auto" }}>
          {addOpen && (
            <div style={{ position: "absolute", bottom: "calc(100% + 8px)", left: 0, right: 0, background: "#101828", border: "1px solid #26304a", borderRadius: 10, padding: 8, display: "flex", flexDirection: "column", gap: 6, zIndex: 10, boxShadow: "0 8px 24px rgba(0,0,0,0.5)" }}>
              {/* From the rated pool: pick a strength, get a bot measured at it. */}
              <div style={{ display: "flex", alignItems: "center", gap: 8, padding: "6px 8px", borderBottom: "1px solid #26304a", marginBottom: 4 }}>
                <span style={{ fontSize: 13, color: "#8fa0c0", flex: "0 0 auto" }}>Rated</span>
                <input type="range" min={Math.floor(POOL_ELO_MIN / 50) * 50} max={Math.ceil(POOL_ELO_MAX / 50) * 50} step={50} value={poolElo} onChange={(e) => setPoolElo(Number(e.target.value))} style={{ flex: 1, minWidth: 0 }} />
                <span style={{ width: 40, textAlign: "right", fontVariantNumeric: "tabular-nums", color: "#f5c542", fontWeight: 700, fontSize: 13 }}>{poolElo}</span>
                <button onClick={() => { addPoolBot(poolElo); setAddOpen(false); }} style={{ ...btnStyle, padding: "6px 10px", fontSize: 13, color: "#f5c542", borderColor: "#f5c54266" }}>+ Elo bot</button>
              </div>
              <div style={{ fontSize: 11, color: "#6b7590", padding: "0 8px 6px" }}>{BOT_POOL.length} rated bots, {POOL_ELO_MIN}–{POOL_ELO_MAX}</div>
              {Object.keys(PRESETS).map((p) => (
                <button key={p} onClick={() => { addBot(p); setAddOpen(false); }} style={{ ...btnStyle, textAlign: "left", padding: "8px 12px", fontSize: 14 }}>
                  <b>{p}</b>
                  <span style={{ color: "#8fa0c0", marginLeft: 8, fontSize: 12 }}>
                    {Object.entries(PRESETS[p]).map(([k, v]) => k + " " + ((v as number) > 0 ? "+" : "") + v).join(" · ") || "no traits"}
                  </span>
                </button>
              ))}
            </div>
          )}
          <button onClick={() => setAddOpen((v) => !v)} style={{ ...btnStyle, width: "100%", padding: "10px 0", fontSize: 15, fontWeight: 700, letterSpacing: 1 }}>
            {addOpen ? "CANCEL" : "ADD BOT"}
          </button>
        </div>
      </div>
    </div>
  );
}

const selectStyle: React.CSSProperties = {
  background: "#141b2e", color: "#cbd3e6", border: "1px solid #26304a", borderRadius: 6,
  padding: "3px 6px", fontSize: 12, fontFamily: "'Rajdhani',sans-serif", width: 96,
};

// Bigger arena-side controls (restart / pause), icon + label. Fixed height so
// differing icon sizes (and the replay scrubber) can't change the row height.
const BOARD_BTN_H = 40;
const boardBtnStyle: React.CSSProperties = {
  background: "#141b2e", color: "#cbd3e6", border: "1px solid #26304a", borderRadius: 8,
  height: BOARD_BTN_H, boxSizing: "border-box", padding: "0 20px", fontSize: 15, fontWeight: 700, cursor: "pointer",
  fontFamily: "'Rajdhani',sans-serif", display: "flex", alignItems: "center",
  justifyContent: "center", gap: 8, letterSpacing: 0.5,
};

const btnStyle: React.CSSProperties = {
  background: "#141b2e", color: "#cbd3e6", border: "1px solid #26304a", borderRadius: 6,
  padding: "3px 10px", fontSize: 12, cursor: "pointer", fontFamily: "'Rajdhani',sans-serif",
};
